import React, { useState } from "react";
import { motion } from "framer-motion";

const SkinTemperatureGraph: React.FC = () => {
    const [activeTab, setActiveTab] = useState<"daily" | "cycle">("daily");

    const currentTemp = 36.5;
    const baselineTemp = 36.3;
    const deviation = currentTemp - baselineTemp;

    const dailyTemps = [36.2, 36.3, 36.4, 36.5, 36.6, 36.7, 36.5, 36.4];
    const cycleTemps = [
        { day: 1, temp: 36.2, phase: "قاعدگی" },
        { day: 7, temp: 36.3, phase: "فولیکولار" },
        { day: 14, temp: 36.8, phase: "تخمک‌گذاری" },
        { day: 21, temp: 36.9, phase: "لوتئال" },
        { day: 28, temp: 36.4, phase: "پایان چرخه" },
    ];

    const getTempStatus = (deviation: number) => {
        if (deviation > 0.5) return { text: "بالاتر از حد نرمال", color: "#FF3B30", icon: "⚠️" };
        if (deviation > 0.2) return { text: "کمی بالاتر", color: "#FF9500", icon: "📈" };
        if (deviation < -0.2) return { text: "کمی پایین‌تر", color: "#0072CE", icon: "📉" };
        return { text: "نرمال", color: "#32D74B", icon: "✓" };
    };

    const status = getTempStatus(deviation);

    return (
        <div className="space-y-3" dir="rtl">
            <div>
                <h4 className="font-medium text-[#002B5C] mb-3">دمای پوست امروز</h4>

                {/* Current Temperature Display */}
                <div className="bg-gradient-to-br from-[#ECEFF1]/80 to-[#ECEFF1]/30 p-4 rounded-[16px] mb-3 shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <span className="text-3xl">{status.icon}</span>
                            <div>
                                <div className="text-2xl font-bold text-[#0072CE]">{currentTemp}°C</div>
                                <div className="text-xs text-[#9EA3A9]">دمای فعلی</div>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-sm font-medium" style={{ color: status.color }}>
                                {status.text}
                            </div>
                            <div className="text-xs text-[#9EA3A9]">
                                پایه: {baselineTemp}°C ({deviation > 0 ? "+" : ""}
                                {deviation.toFixed(1)}°C)
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-[#ECEFF1] mb-3">
                    <button
                        className={`px-4 py-2 text-sm font-medium ${activeTab === "daily" ? "text-[#0072CE] border-b-2 border-[#0072CE]" : "text-[#9EA3A9]"}`}
                        onClick={() => setActiveTab("daily")}
                    >
                        روند روزانه
                    </button>
                    <button
                        className={`px-4 py-2 text-sm font-medium ${activeTab === "cycle" ? "text-[#0072CE] border-b-2 border-[#0072CE]" : "text-[#9EA3A9]"}`}
                        onClick={() => setActiveTab("cycle")}
                    >
                        تحلیل چرخه
                    </button>
                </div>

                {/* Daily Trend */}
                {activeTab === "daily" && (
                    <div>
                        <div className="h-24 bg-[#ECEFF1] rounded-[16px] p-3 relative shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                            <div className="absolute inset-0 flex items-end p-3">
                                {dailyTemps.map((temp, i) => {
                                    const height = ((temp - 36) / 1) * 100;
                                    return (
                                        <div key={i} className="flex-1 flex flex-col items-center">
                                            <motion.div
                                                className="w-2 bg-gradient-to-t from-[#FF9500] to-[#FF3B30] rounded-t-[4px]"
                                                initial={{ height: 0 }}
                                                animate={{ height: `${height}%` }}
                                                transition={{ duration: 0.5, delay: i * 0.1 }}
                                            />
                                            <span className="text-[10px] text-[#9EA3A9] mt-1">{["۶", "۹", "۱۲", "۱۵", "۱۸", "۲۱", "۲۴", "۳"][i]}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="flex justify-between text-xs text-[#9EA3A9] mt-1">
                            <span>صبح</span>
                            <span>شب</span>
                        </div>
                    </div>
                )}

                {/* Cycle Analysis */}
                {activeTab === "cycle" && (
                    <div>
                        <div className="h-24 bg-[#ECEFF1] rounded-[16px] p-3 relative shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                            <svg className="w-full h-full" viewBox="0 0 280 80">
                                <motion.path
                                    d={`M 0 ${80 - (cycleTemps[0].temp - 36) * 40} ${cycleTemps.map((d, i) => `L ${i * 70} ${80 - (d.temp - 36) * 40}`).join(" ")}`}
                                    fill="none"
                                    stroke="#FF9500"
                                    strokeWidth="2"
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: 1 }}
                                    transition={{ duration: 1.5 }}
                                />
                                {cycleTemps.map((d, i) => (
                                    <motion.circle
                                        key={i}
                                        cx={i * 70}
                                        cy={80 - (d.temp - 36) * 40}
                                        r="4"
                                        fill="#FF9500"
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ delay: i * 0.2 }}
                                    />
                                ))}
                            </svg>
                        </div>
                        <div className="mt-3 space-y-2">
                            {cycleTemps.map((d, i) => (
                                <div key={i} className="flex items-center justify-between text-xs">
                                    <span className="text-[#4A5568]">
                                        روز {d.day}: {d.phase}
                                    </span>
                                    <span className="font-medium text-[#FF9500]">{d.temp}°C</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Health Insights */}
                <div className="mt-3 p-3 bg-[#FFF4E6] rounded-[16px] border border-[#FF9500]/20">
                    <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-[#FF9500] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                            <h5 className="text-sm font-medium text-[#FF9500] mb-1">بینش سلامت</h5>
                            <p className="text-xs text-[#4A5568]">دمای بدن شما در محدوده طبیعی است. افزایش ناگهانی دما می‌تواند نشانه بیماری یا نزدیکی به دوره تخمک‌گذاری باشد.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SkinTemperatureGraph;
